import { useContext, useMemo } from 'react';
import { notification } from 'antd';

import { useTranslation } from 'react-i18next';

import useSwr from '@hooks/useSwr';
import { AxiosInstance } from 'axios';
import ActionTypes from '@utils/deviceActions';

import { IDevice } from '@pages/Assets/Inventory/types';
import api from '@services/api';
import SessionContext from '@providers/Session/SessionContext';

export interface IDevicePlan {
  name: string;
}

export interface IDeviceConsumption {
  results?: {
    series: {
      columns: string[];
      values: any[];
    }[];
  }[];
}

export interface IDeviceConsumptionData {
  name: string;
  date: string;
  traffic: number;
}

export interface IPrepareSendActionRequest {
  key: ActionTypes;
  device: any | undefined;
  api: AxiosInstance;
  username: string | undefined;
}

export interface IUDeviceFormatterProps {
  dataPlan: IDevicePlan | undefined;
  device: IDevice | undefined;
  dataConsumption: IDeviceConsumption | undefined;
  errorConsumption: any;
  consumptionFormatted: IDeviceConsumptionData[];
  handleUpdateNickname: (value: string) => Promise<any>;
  handleUpdateNote: (value: string) => Promise<any>;
  locationFormatted: string;
  activationDateFormatted: string;
  lastConnectionFormatted: string;
}

export function useDeviceFormatter(
  id: number,
  showContracted?: string,
  handleMutateInventoryData?: any,
): IUDeviceFormatterProps {
  const { t } = useTranslation('inventory-detailed');
  const { selectedApp, isMot } = useContext(SessionContext);

  const routeToFindData = isMot
    ? 'MOT'
    : selectedApp?.split('_').join('').toUpperCase();

  const { data: device, mutate: mutateDevice } = useSwr<IDevice>(
    id ? `/service-proxy/inventory/${routeToFindData}/details` : null,
    {
      method: 'post',
      data: {
        showContracted,
        id,
      },
    },
  );

  const { data: dataPlan } = useSwr<IDevicePlan>(null);

  const { data: dataConsumption, error: errorConsumption } =
    useSwr<IDeviceConsumption>(null);

  const handleUpdateNickname = async (value: string) => {
    if (value !== t('default-nickname')) {
      try {
        await api.put(
          `/service-proxy/inventory/${routeToFindData}/${device?.msisdn}/nickname`,
          undefined,
          {
            params: {
              nickname: value,
            },
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        if (device) {
          mutateDevice({
            ...device,
            nickname: value,
          });
          if (handleMutateInventoryData) {
            handleMutateInventoryData({
              ...device,
              nickname: value,
            });
          }
        }
      } catch {
        notification.error({
          message: t('error'),
          description: t('errors.update-nickname'),
        });
      }
    }
  };

  const handleUpdateObservation = async (value: string) => {
    try {
      await api.put(
        `/service-proxy/inventory/${routeToFindData}/${device?.msisdn}/observation`,
        undefined,
        {
          params: {
            observation: value,
          },
        },
      );

      if (device) {
        mutateDevice({
          ...device,
          observation: value,
        });
        if (handleMutateInventoryData) {
          handleMutateInventoryData({
            ...device,
            observation: value,
          });
        }
      }
      return device;
    } catch {
      notification.error({
        message: t('error'),
        description: t('errors.update-note'),
      });
      return 'error';
    }
  };

  const consumptionFormatted = useMemo(() => {
    const consumption: IDeviceConsumptionData[] = [];
    if (dataConsumption?.results) {
      dataConsumption?.results[0].series[0].values.forEach(values => {
        values.forEach((value: number | null, index: number) => {
          if (index === 0) {
            return;
          }
          if (dataConsumption.results) {
            consumption.push({
              name: dataConsumption.results[0].series[0].columns[index],
              traffic: value || 0,
              date: `${new Date(values[0]).getDate()}/${
                new Date(values[0]).getMonth() + 1
              }`,
            });
          }
        });
      });
    }
    return consumption;
  }, [dataConsumption]);

  const locationFormatted = useMemo(() => {
    const city = device?.city;
    const state = device?.state;
    if (city && state) {
      return `${city}, ${state}`;
    }
    if (city) {
      return city;
    }
    if (state) {
      return state;
    }
    return '-';
  }, [device?.city, device?.state]);

  const activationDateFormatted = useMemo(() => {
    if (device?.activationDate) {
      const date = new Date(device?.activationDate);
      return date.toLocaleString();
    }
    return '';
  }, [device?.activationDate]);

  const lastConnectionFormatted = useMemo(() => {
    if (device?.lastConnection) {
      const date = new Date(device?.lastConnection);
      return date.toLocaleString();
    }
    return '';
  }, [device?.lastConnection]);

  return {
    device,
    dataConsumption,
    errorConsumption,
    consumptionFormatted,
    handleUpdateNickname,
    handleUpdateNote: handleUpdateObservation,
    locationFormatted,
    activationDateFormatted,
    lastConnectionFormatted,
    dataPlan,
  };
}
